/**
 * #1898 — one place that decides what `Authorization` actually goes out.
 *
 * ⚠ THE FAILURE THIS EXISTS FOR. `userToken` is
 * `"Bearer " + state.user.main.token`. When the token is empty the getter still
 * returns the literal string `"Bearer "` — truthy, correctly prefixed, and
 * carrying nothing. The request interceptor only filled the header when one was
 * ABSENT, so an explicitly-supplied `"Bearer "` sailed past it; the server ran
 * `Str::after($header, 'Bearer ')`, got `''`, and answered
 * `401 Token not provided`.
 *
 * It looks perfectly well-formed in the network tab, which is what made it hard
 * to see — and it is why one page could 401 while 144 components doing the same
 * thing worked: they simply had a token in the store at the moment they asked.
 *
 * ⚠ AND A BARE TOKEN GOT NO PREFIX. The same `if (!config.headers.Authorization)`
 * meant any caller passing a bare token bypassed normalisation entirely.
 *
 * Fixing it here rather than at 144 call sites is the point: the next component
 * to pass a header cannot reintroduce either failure.
 */

/** The token inside a header value, or '' when there is not one. */
export function tokenIn(header) {
    const h = String(header === null || header === undefined ? '' : header);
    if (h.trim() === '') {
        return '';
    }

    // ⚠ DO NOT TRIM BEFORE THIS TEST. `'Bearer '.trim()` is `'Bearer'`, which no
    // longer starts with `'Bearer '` — so the scheme gets returned AS the token
    // and the exact value this function exists to catch sails through. My first
    // cut did that and three tests caught it.
    if (h.indexOf('Bearer ') === 0) {
        return h.slice(7).trim();
    }

    const bare = h.trim();

    return bare === 'Bearer' ? '' : bare;
}

/**
 * The header to send.
 *
 * ⚠ THE CALLER'S TOKEN WINS WHENEVER IT HAS ONE. Some callers deliberately send
 * a different token — an impersonation, a refresh probe — and replacing a real
 * supplied token with the store's would break them silently. The store is a
 * fallback for an EMPTY one, never an override.
 *
 * @param {string} supplied    what the component put on the request
 * @param {string} storeToken  the store's current `userToken`
 * @returns {string}           '' when there is nothing to send
 */
export function resolve(supplied, storeToken) {
    const own = tokenIn(supplied);
    const token = own !== '' ? own : tokenIn(storeToken);

    return token === '' ? '' : 'Bearer ' + token;
}
